/**
 * Funzioni API per il product-service.
 * Usano createServiceClient(SERVICE_URLS.product) come da pattern slice 1.
 * Ogni funzione corrisponde a un endpoint REST del product-service.
 */

import { createServiceClient } from '../../api/client.js';
import type { CycloneDxBom } from './sbomDiff.js';
import type { ReleaseComparison } from './releaseCompare.js';
import type {
  ActiveBundle,
  BomArtifact,
  BomArtifactScope,
  BulkDeliveryInput,
  ComponentBomAttachInput,
  ComponentBomWithArtifact,
  CreateComponentInput,
  CreateDeploymentInput,
  CreateProductInput,
  CreateReleaseInput,
  CsvImportSummary,
  CustomerVulnNotification,
  Deployment,
  IdentifierMapping,
  ImpactResponse,
  MappingInput,
  NotifyAllInput,
  NotifyAllResponse,
  NotifyInput,
  OpenVulnBundle,
  OwnershipInput,
  PatchDeploymentInput,
  Product,
  ProductOwnership,
  Release,
  ReleaseComponent,
  UpdateProductInput,
} from './types.js';

/** Client parzialmente applicato per il product-service. */
const productApi = createServiceClient('product');

// ---------------------------------------------------------------------------
// Prodotti
// ---------------------------------------------------------------------------

/** Recupera la lista di tutti i prodotti. */
export function fetchProducts(): Promise<Product[]> {
  return productApi<Product[]>('/products');
}

/** Recupera un singolo prodotto per ID interno. Lancia ApiError 404 se assente. */
export function fetchProduct(id: string): Promise<Product> {
  return productApi<Product>(`/products/${id}`);
}

/** Crea un nuovo prodotto. */
export function createProduct(input: CreateProductInput): Promise<Product> {
  return productApi<Product>('/products', { method: 'POST', body: input });
}

/** Aggiorna parzialmente un prodotto. */
export function updateProduct(id: string, input: UpdateProductInput): Promise<Product> {
  return productApi<Product>(`/products/${id}`, { method: 'PATCH', body: input });
}

/** Soft-delete di un prodotto (risposta 204 → undefined). */
export function deleteProduct(id: string): Promise<void> {
  return productApi<void>(`/products/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Release
// ---------------------------------------------------------------------------

/** Recupera le release di un prodotto. */
export function fetchReleases(productId: string): Promise<Release[]> {
  return productApi<Release[]>(`/products/${productId}/releases`);
}

/**
 * Confronto profondo fra due release del prodotto (metadati, bundle, BOM, componenti).
 * GET /products/:id/releases/compare?from=&to=
 */
export function fetchReleaseComparison(
  productId: string,
  from: string,
  to: string,
): Promise<ReleaseComparison> {
  const qs = new URLSearchParams({ from, to });
  return productApi<ReleaseComparison>(
    `/products/${encodeURIComponent(productId)}/releases/compare?${qs.toString()}`,
  );
}

/** Crea una nuova release per un prodotto. */
export function createRelease(productId: string, input: CreateReleaseInput): Promise<Release> {
  return productApi<Release>(`/products/${productId}/releases`, { method: 'POST', body: input });
}

/** Release archiviate del prodotto (GET /products/:id/releases?archived=only). */
export function fetchArchivedReleases(productId: string): Promise<Release[]> {
  return productApi<Release[]>(`/products/${productId}/releases?archived=only`);
}

/**
 * Archivia una release SENZA forniture (409 RELEASE_HAS_DEPLOYMENTS altrimenti) o la ripristina.
 * POST /products/:id/releases/:version/archive | unarchive
 */
export function setReleaseArchived(
  productId: string,
  version: string,
  archived: boolean,
): Promise<Release> {
  return productApi<Release>(
    `/products/${productId}/releases/${encodeURIComponent(version)}/${archived ? 'archive' : 'unarchive'}`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// Mapping identificatori (CPE/PURL)
// ---------------------------------------------------------------------------

/** Recupera i mapping di un prodotto. */
export function fetchMappings(productId: string): Promise<IdentifierMapping[]> {
  return productApi<IdentifierMapping[]>(`/products/${productId}/mappings`);
}

/** Aggiunge un mapping a un prodotto. */
export function addMapping(productId: string, input: MappingInput): Promise<IdentifierMapping> {
  return productApi<IdentifierMapping>(`/products/${productId}/mappings`, {
    method: 'POST',
    body: input,
  });
}

/** Rimuove un mapping da un prodotto (204 → undefined). */
export function removeMapping(productId: string, input: MappingInput): Promise<void> {
  return productApi<void>(`/products/${productId}/mappings`, { method: 'DELETE', body: input });
}

/** Import CSV mapping (dry-run o applicato). */
export function importMappingsCsv(csv: string, dryRun: boolean): Promise<CsvImportSummary> {
  return productApi<CsvImportSummary>(
    `/products/mappings/import?dryRun=${dryRun ? 'true' : 'false'}`,
    { method: 'POST', body: { csv } },
  );
}

// ---------------------------------------------------------------------------
// Bubble-up vulnerabilità aperte (Slice 8c)
// ---------------------------------------------------------------------------

/**
 * Scarica TUTTI i bundle con CVE aperte (GET /bundles-with-open-cves, paginato).
 * Fonte unica per badge + ordinamento: dal linkage si derivano Product/Release/Bundle affetti.
 */
export async function fetchOpenVulnBundles(): Promise<OpenVulnBundle[]> {
  const items: OpenVulnBundle[] = [];
  let page = 1;
  const pageSize = 200;
  for (;;) {
    const res = await productApi<{ items?: OpenVulnBundle[]; total?: number }>(
      `/bundles-with-open-cves?page=${page}&pageSize=${pageSize}`,
    );
    const batch = res.items ?? [];
    items.push(...batch);
    if (batch.length === 0 || items.length >= (res.total ?? items.length)) break;
    page += 1;
  }
  return items;
}

/**
 * Scarica TUTTI i bundle "scansione in sospeso" (GET /bundles-pending-scan, paginato): bundle
 * modificati dopo l'ultima scansione (o mai scansionati). Dal linkage si derivano i set
 * Product/Release/Bundle pending per il badge. Stesso pattern di paginazione di fetchBundles.
 */
export async function fetchPendingScanBundles(): Promise<ActiveBundle[]> {
  const items: ActiveBundle[] = [];
  let page = 1;
  const pageSize = 200;
  for (;;) {
    const res = await productApi<{ items?: ActiveBundle[]; total?: number }>(
      `/bundles-pending-scan?page=${page}&pageSize=${pageSize}`,
    );
    const batch = res.items ?? [];
    items.push(...batch);
    if (batch.length === 0 || items.length >= (res.total ?? items.length)) break;
    page += 1;
  }
  return items;
}

/**
 * Scarica TUTTI i bundle attivi (GET /bundles, paginato). Linkage Release/Product per risolvere
 * il breadcrumb di una foglia (anche quando non ha CVE aperte). Stesso pattern di paginazione di
 * fetchOpenVulnBundles.
 */
export async function fetchBundles(): Promise<ActiveBundle[]> {
  const items: ActiveBundle[] = [];
  let page = 1;
  const pageSize = 200;
  for (;;) {
    const res = await productApi<{ items?: ActiveBundle[]; total?: number }>(
      `/bundles?page=${page}&pageSize=${pageSize}`,
    );
    const batch = res.items ?? [];
    items.push(...batch);
    if (batch.length === 0 || items.length >= (res.total ?? items.length)) break;
    page += 1;
  }
  return items;
}

// ---------------------------------------------------------------------------
// SBOM (per-bundle: i finding/componenti vivono sulla foglia DT — Slice 7d)
// ---------------------------------------------------------------------------

/**
 * Recupera il CONTENUTO del SBOM di un BUNDLE (export CycloneDX dalla foglia DT) per
 * visualizzazione/confronto. 404 se il bundle non è ancora sincronizzato con DT.
 */
export function fetchSbomContent(
  productId: string,
  version: string,
  componentId: number,
): Promise<CycloneDxBom> {
  return productApi<CycloneDxBom>(
    `/products/${productId}/releases/${version}/components/${componentId}/sbom/content`,
  );
}

// ---------------------------------------------------------------------------
// Componenti Release + libreria BOM (Slice 4/5)
// ---------------------------------------------------------------------------

/** Recupera i componenti di una release, con i relativi BOM (metadati artefatto + dtToken). */
export function fetchComponents(productId: string, version: string): Promise<ReleaseComponent[]> {
  return productApi<ReleaseComponent[]>(`/products/${productId}/releases/${version}/components`);
}

/** Crea un nuovo componente per una release. */
export function createComponent(
  productId: string,
  version: string,
  input: CreateComponentInput,
): Promise<ReleaseComponent> {
  return productApi<ReleaseComponent>(`/products/${productId}/releases/${version}/components`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Attacca un BOM a un componente: artefatto esistente (bomArtifactId) oppure nuovo
 * upload (bomBase64 + format [+ filename]).
 */
export function attachComponentBom(
  productId: string,
  version: string,
  componentId: number,
  input: ComponentBomAttachInput,
): Promise<ComponentBomWithArtifact> {
  return productApi<ComponentBomWithArtifact>(
    `/products/${productId}/releases/${version}/components/${componentId}/boms`,
    { method: 'POST', body: input },
  );
}

/** Stacca (soft-delete) un BOM da un componente. */
export function detachComponentBom(
  productId: string,
  version: string,
  componentId: number,
  membershipId: number,
): Promise<void> {
  return productApi<void>(
    `/products/${productId}/releases/${version}/components/${componentId}/boms/${membershipId}`,
    { method: 'DELETE' },
  );
}

/**
 * Recupera la libreria artefatti BOM. scope='product' → artefatti usati dal prodotto
 * indicato; scope='mine' → artefatti caricati dall'utente corrente (uploadedAt DESC).
 * `q` filtra per filename (substring case-insensitive).
 */
export function fetchBomArtifacts(params: {
  scope: BomArtifactScope;
  productId?: string;
  q?: string;
}): Promise<BomArtifact[]> {
  const qs = new URLSearchParams();
  qs.set('scope', params.scope);
  if (params.productId) qs.set('productId', params.productId);
  if (params.q) qs.set('q', params.q);
  return productApi<BomArtifact[]>(`/bom-artifacts?${qs.toString()}`);
}

// ---------------------------------------------------------------------------
// Ownership / destinatari notifiche (E14.3)
// ---------------------------------------------------------------------------

/** Recupera i destinatari notifiche (ownership) configurati per un prodotto. */
export function fetchOwnership(productId: string): Promise<ProductOwnership[]> {
  return productApi<ProductOwnership[]>(`/products/${productId}/ownership`);
}

/** Aggiunge un destinatario all'ownership di un prodotto. 409 se già presente. */
export function addOwnership(productId: string, input: OwnershipInput): Promise<ProductOwnership> {
  return productApi<ProductOwnership>(`/products/${productId}/ownership`, {
    method: 'POST',
    body: input,
  });
}

/** Rimuove (soft-delete) un destinatario dall'ownership di un prodotto (204 → undefined). */
export function removeOwnership(productId: string, entryId: number): Promise<void> {
  return productApi<void>(`/products/${productId}/ownership/${entryId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Forniture / Deployments (Slice B)
// ---------------------------------------------------------------------------

/**
 * Recupera le consegne di un prodotto.
 * Se currentOnly=true viene aggiunto il query param ?current=true.
 */
export function fetchDeployments(productId: string, currentOnly = false): Promise<Deployment[]> {
  const qs = currentOnly ? '?current=true' : '';
  return productApi<Deployment[]>(`/products/${productId}/deployments${qs}`);
}

/** Crea una nuova consegna per un prodotto. 201 con la consegna creata. */
export function createDeployment(
  productId: string,
  input: CreateDeploymentInput,
): Promise<Deployment> {
  return productApi<Deployment>(`/products/${productId}/deployments`, {
    method: 'POST',
    body: input,
  });
}

/** Aggiorna label/description di una consegna (campi mutabili). */
export function patchDeployment(
  productId: string,
  depId: number,
  input: PatchDeploymentInput,
): Promise<Deployment> {
  return productApi<Deployment>(`/products/${productId}/deployments/${depId}`, {
    method: 'PATCH',
    body: input,
  });
}

/** Soft-delete di una consegna (204 → undefined). */
export function deleteDeployment(productId: string, depId: number): Promise<void> {
  return productApi<void>(`/products/${productId}/deployments/${depId}`, { method: 'DELETE' });
}

/**
 * Push massivo di consegne: crea una consegna per ogni cliente/sito con la release
 * indicata come "versione precedente" (o selezionata esplicitamente).
 * POST /products/:id/releases/:version/deliveries
 */
export function bulkDeliver(
  productId: string,
  version: string,
  input: BulkDeliveryInput,
): Promise<Deployment[]> {
  return productApi<Deployment[]>(`/products/${productId}/releases/${version}/deliveries`, {
    method: 'POST',
    body: input,
  });
}

// ---------------------------------------------------------------------------
// Impatto + notifiche manuali (Slice C)
// ---------------------------------------------------------------------------

/**
 * Recupera i clienti impattati da una release (consegna corrente = version) e lo
 * storico ledger per (product, release).
 */
export function fetchImpact(productId: string, version: string): Promise<ImpactResponse> {
  return productApi<ImpactResponse>(`/products/${productId}/releases/${version}/impact`);
}

/**
 * Notifica manualmente un singolo cliente impattato per il finding indicato.
 * 502 se l'invio fallisce (riga ledger comunque registrata con status='failed').
 */
export function notifyCustomer(
  productId: string,
  version: string,
  vulnId: string,
  input: NotifyInput,
): Promise<CustomerVulnNotification> {
  return productApi<CustomerVulnNotification>(
    `/products/${productId}/releases/${version}/findings/${encodeURIComponent(vulnId)}/notify`,
    { method: 'POST', body: input },
  );
}

/** Notifica manualmente tutti i clienti impattati per il finding indicato. */
export function notifyAllImpacted(
  productId: string,
  version: string,
  vulnId: string,
  input: NotifyAllInput,
): Promise<NotifyAllResponse> {
  return productApi<NotifyAllResponse>(
    `/products/${productId}/releases/${version}/findings/${encodeURIComponent(vulnId)}/notify-all`,
    { method: 'POST', body: input },
  );
}

/** Recupera lo storico invii (ledger) per (product, release), piu' recenti prima. */
export function fetchNotifications(
  productId: string,
  version: string,
): Promise<CustomerVulnNotification[]> {
  return productApi<CustomerVulnNotification[]>(
    `/products/${productId}/releases/${version}/notifications`,
  );
}
